import { Resources } from 'constants/resources';

import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { usePermissions } from 'hooks/usePermissions';
import { useYupSchema } from 'pages/Merchants/MerchantsShow/MerchantsShowLimits/hooks/useYupSchema';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Form, RecordContextProvider, useNotify, useUpdate } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Button, DeleteButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Limit } from 'types/embedded/limit';
import { Permission } from 'types/embedded/permission';

import { CardContentStyled } from './MerchantsShowLimitsEditDialog.styled';
import { MerchantsShowLimitsEditDialogForm } from './MerchantsShowLimitsEditDialogForm/MerchantsShowLimitsEditDialogForm';

type Props = {
  open: boolean;
  onClose: () => void;
  merchantId: string | undefined;
  onDelete: (record: Limit) => void;
  record: Limit | undefined;
};

interface FormValues extends FieldValues {
  currency: string;
  gateway: string;
  paymentMax: number;
  paymentMin: number;
  payoutMax: number;
  payoutMin: number;
}

const transform = (data: FormValues) => ({
  ...data,
  paymentMax: data.paymentMax ? data.paymentMax + '' : null,
  paymentMin: data.paymentMin ? data.paymentMin + '' : null,
  payoutMax: data.payoutMax ? data.payoutMax + '' : null,
  payoutMin: data.payoutMin ? data.payoutMin + '' : null,
});

export const MerchantsShowLimitsEditDialog: React.FC<Props> = ({
  open,
  onClose,
  onDelete,
  record,
  merchantId,
}) => {
  const translate = useTypedTranslate();
  const yupSchema = useYupSchema();

  const { hasResourcePermissions } = usePermissions();

  const [update] = useUpdate();
  const notify = useNotify();
  const [errors, setErrors] = useState<string[]>([]);

  const onSubmit = (formValues: FormValues) => {
    update(
      Resources.Limits,
      {
        id: record?.id,
        data: {
          ...transform(formValues),
          merchant: merchantId,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify(translate('app.limits.limitUpdatedSuccess'), {
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          setErrors((error as Error)?.message.split('\n'));
          notify(translate('app.limits.formInvalid'), { type: 'error' });
        },
      },
    );
  };

  return (
    <Dialog onClose={onClose} open={open} scroll="body" width="640px">
      <RecordContextProvider value={record}>
        <Form onSubmit={onSubmit as any} resolver={yupResolver<FieldValues>(yupSchema)}>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.limits.editLimit')}
            titleSize="smallBold"
          />
          <CardContentStyled>
            <MerchantsShowLimitsEditDialogForm errors={errors} merchantId={merchantId} />
          </CardContentStyled>
          <CardActionsButtons
            leftActionsSlot={
              hasResourcePermissions(Resources.Limits, [Permission.Full, Permission.Delete]) && (
                <DeleteButton
                  onClick={() => {
                    onClose();
                    record && onDelete(record);
                  }}
                  variant="outlined"
                />
              )
            }
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <SaveButton key="update" label={translate('app.buttons.save')} />,
            ]}
          />
        </Form>
      </RecordContextProvider>
    </Dialog>
  );
};
